import { db } from "../../firebase/config";
import { doc, setDoc, getDoc, collection, query, where, getDocs, serverTimestamp } from "firebase/firestore";

/**
 * Récupère la liste des agriculteurs en attente d'approbation
 * @returns {Promise<Array>} Liste des agriculteurs en attente
 */
export const getPendingFarmers = async () => {
  try {
    const pendingQuery = query(
      collection(db, "farmers"),
      where("emailVerified", "==", true),
      where("status", "==", "pending")
    );
    
    const snapshot = await getDocs(pendingQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des agriculteurs en attente:", error);
    throw error;
  }
};

/**
 * Approuve un agriculteur par un administrateur
 * @param {string} farmerId - ID de l'agriculteur à approuver
 * @param {string} adminId - ID de l'administrateur qui approuve
 * @returns {Promise<Object>} Résultat de l'opération
 */
export const approveFarmer = async (farmerId, adminId) => {
  try {
    const farmerRef = doc(db, "farmers", farmerId);
    const farmerDoc = await getDoc(farmerRef);
    
    if (!farmerDoc.exists()) {
      return {
        success: false,
        message: "Agriculteur non trouvé"
      };
    }
    
    await setDoc(farmerRef, {
      adminApproved: true,
      status: "approved", // Mettre à jour le statut à 'approved'
      approvedBy: adminId,
      approvedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return {
      success: true,
      message: "Agriculteur approuvé avec succès"
    };
  } catch (error) {
    console.error("Erreur lors de l'approbation de l'agriculteur:", error);
    return {
      success: false,
      message: "Erreur lors de l'approbation: " + error.message
    };
  }
};

/**
 * Rejette un agriculteur par un administrateur
 * @param {string} farmerId - ID de l'agriculteur à rejeter
 * @param {string} adminId - ID de l'administrateur qui rejette
 * @param {string} reason - Raison du rejet
 * @returns {Promise<Object>} Résultat de l'opération
 */
export const rejectFarmer = async (farmerId, adminId, reason = "") => {
  try {
    const farmerRef = doc(db, "farmers", farmerId);
    const farmerDoc = await getDoc(farmerRef);
    
    if (!farmerDoc.exists()) {
      return {
        success: false,
        message: "Agriculteur non trouvé"
      };
    }
    
    await setDoc(farmerRef, {
      adminApproved: false,
      status: "rejected", // Mettre à jour le statut à 'rejected'
      rejected: true,
      rejectedBy: adminId,
      rejectionReason: reason,
      rejectedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    return {
      success: true,
      message: "Demande d'agriculteur rejetée"
    };
  } catch (error) {
    console.error("Erreur lors du rejet de l'agriculteur:", error);
    return {
      success: false,
      message: "Erreur lors du rejet: " + error.message
    };
  }
};

/**
 * Récupère le statut d'approbation d'un agriculteur
 * @param {string} farmerId - ID de l'agriculteur
 * @returns {Promise<Object>} Statut d'approbation
 */
export const getFarmerApprovalStatus = async (farmerId) => {
  try {
    const farmerRef = doc(db, "farmers", farmerId);
    const farmerDoc = await getDoc(farmerRef);
    
    if (!farmerDoc.exists()) {
      return {
        exists: false,
        message: "Agriculteur non trouvé"
      };
    }
    
    const data = farmerDoc.data();
    
    return {
      exists: true,
      emailVerified: data.emailVerified || false,
      adminApproved: data.adminApproved || false,
      rejected: data.rejected || false,
      rejectionReason: data.rejectionReason || "",
      approvedAt: data.approvedAt,
      rejectedAt: data.rejectedAt
    };
  } catch (error) {
    console.error("Erreur lors de la récupération du statut d'approbation:", error);
    throw error;
  }
};